import { useQuery } from '@tanstack/react-query';
import { useCustomInstance } from '@/lib/use-custom-instance';
import type { PublicFooter } from './useFooter';

/**
 * Published Buy In popup footer for the resolved country, falling back to the
 * Buy In Default (server-side) via `GET /buy-in-footer`. Mirrors {@link useFooter}
 * but targets the separate Buy In footer resource so the disclaimer shown in the
 * Buy In popup is admin-editable through its own CMS page.
 *
 * `countryCode` comes from `useTerritory()` when known (KYC-verified players);
 * omit it for signed-out / unresolved players to get the Default.
 *
 * INTERIM data layer (mirrors `useFooter`): once the api ships the
 * `/buy-in-footer` route to the OpenAPI spec, `yarn generate-api` will emit a
 * typed hook — both funnel through the same `useCustomInstance` mutator, so the
 * swap is mechanical.
 */
export function useBuyInFooter(countryCode?: string) {
  const request = useCustomInstance<PublicFooter>();
  return useQuery({
    queryKey: ['/buy-in-footer', countryCode ?? null],
    queryFn: () =>
      request({
        url: '/buy-in-footer',
        method: 'GET',
        params: countryCode ? { country: countryCode } : undefined,
      }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
}
